import React, { useMemo, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  Tag,
  HStack,
  SimpleGrid,
  useTheme,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { DataTable } from "@root/components/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import CoursesInProgress from "@root/assets/icons/coursesInProgress.svg";
import CompletedCourses from "@root/assets/icons/completedCourses.svg";
import CertificatesEarned from "@root/assets/icons/certificatesIssued.svg";
import CheckMark from "@root/assets/icons/checkMark.svg";
import RecentIcon from "@root/assets/icons/recentIcon.svg";
import { useMemberDetailsContext } from "@root/context/member-detail-context";
import { formatDate, formatSecondsToTime } from "@root/utils/date-helper";
import { StatusLabel } from "../styles";
import {
  COURSE_ENROLLMENT_STATUS,
  JOURNALIST_COURSE_STATUS,
} from "@root/constants";

export const MemberDetailsModal = ({
  isOpen,
  onClose,
  member,
}: MemberDetailsModalProps) => {
  const {
    memberData,
    memberHistory,
    fetchMemberHistory,
    count,
    isMemberDataLoading,
    fetchMemberDetails,
    pageNo,
    setPageNo,
    pageSize,
    isMemberHistoryLoading,
  } = useMemberDetailsContext();

  const handlePageChange = (newPage: number) => {
    if (!member?.id) return;
    setPageNo(newPage);
    const payload = {
      offset: (newPage - 1) * pageSize,
      limit: pageSize,
      statuses: [
        COURSE_ENROLLMENT_STATUS.COMPLETED,
        COURSE_ENROLLMENT_STATUS.ENROLLED,
      ].join(","),
    };
    fetchMemberHistory(member?.id, payload);
  };

  useEffect(() => {
    if (member?.id) {
      setPageNo(1);
      const payload = {
        offset: 0,
        limit: pageSize,
        statuses: [
          COURSE_ENROLLMENT_STATUS.COMPLETED,
          COURSE_ENROLLMENT_STATUS.ENROLLED,
        ].join(","),
      };
      fetchMemberDetails(member?.id);
      fetchMemberHistory(member?.id, payload);
    }
  }, [member?.id]);

  const memberStats = [
    {
      label: "memberDetailsDrawer.coursesInProgress",
      value: memberData?.noOfInprogressCourse,
      icon: CoursesInProgress,
      bgColor: "#3A59D11F",
    },
    {
      label: "memberDetailsDrawer.completedCourses",
      value: memberData?.noOfCompletedCourse,
      icon: CompletedCourses,
      bgColor: "#00A1771F",
    },
    {
      label: "memberDetailsDrawer.certificatesEarned",
      value: memberData?.certificates,
      icon: CertificatesEarned,
      bgColor: "#FFB4331A",
    },
  ];

  const noCoursesFound = () => (
    <Flex
      flexDir={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"350px"}
    >
      {/* <Image src={CheckMark} /> */}
      <Text fontSize={"24px"} fontWeight={600} mt={3}>
        {t("journalistRequests.noRecentCourses")}
      </Text>
      <Text fontSize={"16px"} fontWeight={400} mt={1}>
        {t("journalistRequests.enroll")}
      </Text>
    </Flex>
  );

  const { t } = useTranslation("common");
  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        header: t("memberDetailsDrawer.courseTitle"),
        accessorKey: "course.title",
        cell: ({ row }) => row.original.course?.title ?? "-",
      },
      {
        header: t("memberDetailsDrawer.status"),
        accessorKey: "status",
        cell: ({ row }) => {
          const progress = row.original.progress ?? 0;
          return (
            <StatusLabel
              bg={
                progress === 0
                  ? "#F8FAFC"
                  : progress === 100
                  ? "ECFDF3"
                  : "#FFFAEB"
              }
              color={
                progress === 0
                  ? "#667085"
                  : progress === 100
                  ? "#027A48"
                  : " #B54708"
              }
            >
              {progress === 0
                ? `● ${t("memberDetailsDrawer.notStarted")}`
                : progress === 100
                ? `● ${t("memberDetailsDrawer.completed")}`
                : `● ${t("memberDetailsDrawer.inProgress")}`}
            </StatusLabel>
          );
        },
      },
      {
        header: t("memberDetailsDrawer.completionRate"),
        accessorKey: "progress",
        cell: ({ row }) => {
          const progress = row.original.progress ?? 0;
          return (
            <Flex align="center" gap={2} minW="120px">
              <Box w="60%">
                <Box
                  bg="#E4E4FF"
                  borderRadius="8px"
                  h="8px"
                  w="100%"
                  position="relative"
                >
                  <Box
                    bg="#1819B4"
                    borderRadius="8px"
                    h="8px"
                    w={`${progress}%`}
                    position="absolute"
                    top={0}
                    left={0}
                  />
                </Box>
              </Box>
              <Text fontWeight={500}>{progress}%</Text>
            </Flex>
          );
        },
      },
      {
        header: t("memberDetailsDrawer.certification"),
        accessorKey: "certification",
        cell: ({ row }) => {
          const progress = row.original.progress ?? 0;
          const hasCert = progress === 100;
          return (
            <Tag
              color={hasCert ? "green" : "red"}
              bgColor={hasCert ? "#ECFDF3" : "#FEF3F2"}
              borderRadius="full"
              fontWeight={500}
              fontSize="sm"
            >
              {hasCert
                ? t("memberDetailsDrawer.yes")
                : t("memberDetailsDrawer.no")}
            </Tag>
          );
        },
      },

      {
        header: t("memberDetailsDrawer.lastAccessed"),
        accessorKey: "lastAccessedAt",
        cell: ({ row }) =>
          row.original.lastAccessedAt
            ? formatDate(row.original.lastAccessedAt, "YYYY-MM-DD hh:mm A")
            : "-",
      },
    ],
    [t]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        borderRadius="16px"
        bg="white"
        maxW={{ base: "95%", md: "900px" }}
        h={{ base: "90vh", md: "730px" }}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader
          fontWeight="bold"
          fontSize="xl"
          px={{ base: 4, md: 8 }}
          // pt={8}
          pb={0}
        >
          <Flex align="center" gap={4} mb={6}>
            <Avatar size="lg" name={member?.fullName} src={member?.avatar} />
            <Box>
              <Text fontWeight="bold" fontSize="xl">
                {member?.fullName}
              </Text>
              <HStack mt={1}>
                <Tag
                  borderRadius="full"
                  fontWeight={500}
                  fontSize="sm"
                  px={3}
                  py={1}
                  bg="#00A1771A"
                  color="#00A177"
                >
                  {t("memberDetailsDrawer.org")}:{" "}
                  {member?.subscriber?.companyName}
                </Tag>
              </HStack>
            </Box>
          </Flex>
        </ModalHeader>

        <ModalCloseButton top={6} right={6} />

        <ModalBody px={{ base: 4, md: 8 }} pb={8} overflowY="auto" flex="1">
          {/* Spinner for Member Data */}
          {isMemberDataLoading ? (
            <Flex
              height="100%"
              justifyContent="center"
              alignItems="center"
              minH="200px"
              w="100%"
            >
              <Spinner size="lg" />
            </Flex>
          ) : (
            <>
              {/* Member Stats */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                {memberStats?.map((stat, idx) => (
                  <Box
                    key={idx}
                    borderRadius="lg"
                    border="1px solid #E4EBF0"
                    p={4}
                    minH="100px"
                  >
                    <Flex mb={2} justifyContent="space-between">
                      <Text fontSize="sm" color="gray.500">
                        {t(stat?.label)}
                      </Text>
                      <Box bgColor={stat?.bgColor} borderRadius="20px" p={1.5}>
                        <Image src={stat?.icon} boxSize={6} />
                      </Box>
                    </Flex>
                    <Text fontWeight="bold" fontSize="36px" color="#1819B4">
                      {stat?.value?.toString()?.padStart(2, "0")}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>

              {/* Total Learning Hours */}
              <Box
                borderRadius="lg"
                border="1px solid #E4EBF0"
                p={4}
                width="100%"
              >
                <Flex mb={2} justifyContent="space-between">
                  <Text fontSize="sm" color="gray.500">
                    {t("memberDetailsDrawer.totalLearningHours")}
                  </Text>
                  <Box bgColor="#FE68071A" borderRadius="20px" p={1.5}>
                    <Image src={RecentIcon} boxSize={6} />
                  </Box>
                </Flex>
                <Text fontWeight="bold" fontSize="36px" color="#1819B4">
                  {formatSecondsToTime(memberData?.totalLearning)}
                </Text>
              </Box>

              {/* Member History Table */}
              <Box
                flex="1"
                bg="white"
                border="1px solid #E4EBF0"
                borderRadius="16px"
                minW="320px"
                style={{ border: "1px solid #CDD6DC" }}
                mt={4}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  p={4}
                  bg="#F9F8FF"
                  borderTopRadius="16px"
                >
                  <Text fontWeight="bold">
                    {t("memberDetailsDrawer.courseHistory")}
                  </Text>
                </Flex>

                {isMemberHistoryLoading ? (
                  <Flex
                    height="100%"
                    justifyContent="center"
                    alignItems="center"
                    minH="200px"
                    w="100%"
                  >
                    <Spinner size="lg" />
                  </Flex>
                ) : count > 0 ? (
                  <DataTable
                    data={memberHistory}
                    columns={columns}
                    canEditRow={false}
                    getRowId={(row: any) => row?.title}
                    border="none"
                    showPagination={true}
                    page={pageNo}
                    pageCount={count}
                    pageSize={pageSize}
                    handlePageChange={handlePageChange}
                  />
                ) : (
                  noCoursesFound()
                )}
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
